import classNames from 'classnames'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button } from '@lounge-fe/ui-kit/src/components/Button'
import { Accordion } from '@lounge-fe/ui-kit/src/components/Accordion'
import { Section } from './Section'
import type { FilterForm, FilterSection } from '../types'
import type { FilterByFormProps } from './types'

export const Form = ({
  className,
  name,
  sections,
  showActions,
  onClose,
  sortOptions,
}: FilterByFormProps): JSX.Element => {
  const { formState, watch, reset } = useFormContext<FilterForm>()
  const { t } = useTranslation()

  const dirtyValues = watch([
    ...(Object.keys(formState.dirtyFields ?? {})
      .filter((key) => !['sort'].includes(key))
      .map((key) => `${name}.${key}`) as (keyof FilterForm)[]),
  ])

  const dirtyFiltersCount = dirtyValues.flat().length

  const sectionsWithSort = useMemo<FilterSection[]>(
    () => [
      {
        title: t('action.sort_by'),
        filters: [
          {
            title: t('action.sort_by'),
            titleHidden: true,
            name: 'sort',
            type: 'radiogroup',
            values: sortOptions,
          },
        ],
      } as FilterSection,
      ...sections,
    ],
    [sortOptions, sections],
  )

  return (
    <form
      className={classNames(
        'flex flex-col relative overflow-y-auto overflow-x-hidden',
        className,
      )}
      onSubmit={(evt) => {
        evt.preventDefault()
      }}
    >
      <div className="pb-[145px]">
        <Accordion exclusive variant="icon" iconPosition="end">
          <div>
            {sectionsWithSort.map((section, idx) => (
              <Section key={idx} section={section} initialOpen={idx === 0} />
            ))}
          </div>
        </Accordion>
      </div>
      {showActions && (
        <div
          className="
            grid grid-cols-1 gap-4 pt-4 pb-8 px-6 fixed w-full bottom-0 left-0 right-0
            border-t bg-white border-primary-default"
        >
          <Button type="submit" onClick={onClose}>
            {t('action.apply')}
            {dirtyFiltersCount > 0 && ` (${dirtyFiltersCount})`}
          </Button>
          <Button
            type="reset"
            variant="outline"
            onClick={() => {
              reset()
            }}
          >
            {t('action.clear')}
          </Button>
        </div>
      )}
    </form>
  )
}
